import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileText, Unlock, Mail, ChevronDown, ChevronLeft, ChevronRight, Ban, RotateCcw, BellRing } from "lucide-react";
import { Stars, receiptUrl } from "@/components/EventReport";

function ReportSettings() {
  const [s, setS] = useState(null);
  const [email, setEmail] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/report-settings").then((r) => { setS(r.data); setEmail(r.data.extra_email); }); }, []);
  const save = async () => {
    try { await api.put("/report-settings", { extra_email: email }); toast.success("Indirizzo salvato"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  if (!s) return null;
  return (
    <div className="space-y-2">
    <Button size="sm" variant="outline" data-testid="report-settings-toggle" onClick={() => setOpen(!open)} className="border-slate-700">
      <Mail className="w-4 h-4 mr-1.5 text-cyan-400" /> Email destinatari report <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${open ? "rotate-180" : ""}`} />
    </Button>
    {open && <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 space-y-2" data-testid="report-settings">
      <p className="text-sm"><Mail className="w-4 h-4 text-cyan-400 inline mr-1.5 -mt-0.5" />I report arrivano a <strong className="break-all">{s.manager_email}</strong> e anche a:</p>
      <div className="flex gap-2">
        <Input data-testid="report-extra-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="altro indirizzo email (opzionale)" className="bg-slate-800 border-slate-700" />
        <Button data-testid="report-extra-email-save" onClick={save} className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">Salva</Button>
      </div>
    </div>}
    </div>
  );
}

function ReportCard({ r, onUnlock }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4" data-testid={`report-card-${r.id}`}>
      <button className="w-full flex items-center gap-3 text-left" onClick={() => setOpen(!open)} data-testid={`report-toggle-${r.id}`}>
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{r.event_name} <span className="text-slate-500 font-normal">· {r.technician_name}</span></p>
          <p className="text-xs text-slate-500">Inviato il {new Date(r.submitted_at).toLocaleString("it-IT", { timeZone: "Europe/Rome", dateStyle: "short", timeStyle: "short" })}{r.expenses_total > 0 && ` · spese € ${r.expenses_total.toFixed(2)}`}{r.problems && " · problemi segnalati"}</p>
        </div>
        <Stars value={r.rating} size="w-4 h-4" />
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2 text-sm">
          <p><b>Orari:</b> {r.days.map((d) => `${formatDate(d.date)} ${d.start || "–"}→${d.end || "–"}`).join(" · ") || "non indicati"}</p>
          <p className="whitespace-pre-wrap"><b>Problemi:</b> {r.problems || "nessuno"}</p>
          {r.notes && <p className="whitespace-pre-wrap"><b>Note:</b> {r.notes}</p>}
          <div><b>Spese:</b> {r.expenses.length ? r.expenses.map((e, i) => <div key={i}>{e.description}: € {e.amount.toFixed(2)}</div>) : "nessuna"}</div>
          {r.receipts.length > 0 && <div className="flex flex-wrap gap-2">{r.receipts.map((x) => (
            <a key={x.id} href={receiptUrl(x)} target="_blank" rel="noopener noreferrer" data-testid={`report-receipt-link-${x.id}`} className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"><FileText className="w-3.5 h-3.5" />{x.filename}</a>))}</div>}
          {r.locked ? <Button size="sm" variant="outline" data-testid={`report-unlock-${r.id}`} onClick={() => onUnlock(r.id)} className="border-slate-700"><Unlock className="w-4 h-4 mr-1" /> Sblocca per correzioni</Button>
            : <p className="text-xs text-amber-300">Sbloccato: il tecnico può correggerlo e reinviarlo.</p>}
        </div>
      )}
    </div>
  );
}

function MissingReports({ list, onToggle, onNudge }) {
  if (!list?.length) return <p className="text-sm text-slate-500" data-testid="missing-empty">Nessun report da ricevere.</p>;
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3" data-testid="missing-reports">
      <p className="text-sm font-semibold text-amber-200">Report non ancora inviati</p>
      {list.map((ev) => (
        <div key={ev.event_id} data-testid={`missing-event-${ev.event_id}`}>
          <p className="text-sm font-medium">{ev.event_name} <span className="text-slate-500 font-normal">· {formatDate(ev.end_date)}</span></p>
          <div className="mt-1 space-y-1">
            {ev.technicians.map((t) => (
              <div key={t.technician_id} className="flex items-center gap-2 text-sm" data-testid={`missing-row-${ev.event_id}-${t.technician_id}`}>
                <span className={`flex-1 min-w-0 ${t.waived ? "line-through text-slate-500" : ""}`}>{t.technician_name}{t.waived && <span className="no-underline ml-2 text-xs text-slate-500">(report annullato)</span>}
                  {!t.waived && t.nudged_at && <span className="block text-[11px] text-slate-500">sollecitato il {new Date(t.nudged_at).toLocaleString("it-IT", { timeZone: "Europe/Rome", dateStyle: "short", timeStyle: "short" })}</span>}</span>
                {!t.waived && (
                  <Button size="sm" variant="ghost" data-testid={`missing-nudge-${ev.event_id}-${t.technician_id}`} onClick={() => onNudge(ev, t)}
                    className="text-amber-300 hover:text-amber-200"><BellRing className="w-4 h-4 mr-1" /> Sollecita</Button>
                )}
                <Button size="sm" variant="ghost" data-testid={`missing-toggle-${ev.event_id}-${t.technician_id}`} onClick={() => onToggle(ev, t)}
                  className={t.waived ? "text-cyan-300 hover:text-cyan-200" : "text-rose-300 hover:text-rose-200"}>
                  {t.waived ? <><RotateCcw className="w-4 h-4 mr-1" /> Richiedi di nuovo</> : <><Ban className="w-4 h-4 mr-1" /> Annulla report</>}
                </Button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ReportsPanel({ onChange }) {
  const [list, setList] = useState(null);
  const [missing, setMissing] = useState([]);
  const [sub, setSub] = useState("pending");
  const now = new Date();
  const [ym, setYm] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const shift = (d) => { const [yy, mm] = ym.split("-").map(Number); const x = new Date(yy, mm - 1 + d, 1); setYm(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`); };
  const countOf = (evs) => evs.reduce((n, ev) => n + ev.technicians.filter((t) => !t.waived).length, 0);
  const monthMissing = missing.filter((ev) => (ev.end_date || "").startsWith(ym));
  const monthList = (list || []).filter((r) => (r.event_date || r.submitted_at || "").startsWith(ym));
  const pendingCount = countOf(monthMissing);
  const otherPending = countOf(missing) - pendingCount;
  const load = () => {
    api.get("/reports").then((r) => setList(r.data)).catch(() => setList([]));
    api.get("/reports/missing").then((r) => { setMissing(r.data); onChange?.(); }).catch(() => {});
  };
  const nudge = async (ev, t) => {
    try { await api.post("/reports/nudge", { event_id: ev.event_id, technician_id: t.technician_id }); toast.success(`Sollecito inviato a ${t.technician_name}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggle = async (ev, t) => {
    if (!t.waived && !window.confirm(`Annullare il report di ${t.technician_name} per ${ev.event_name}? Non dovrà più compilarlo e non riceverà promemoria.`)) return;
    try { await api.put("/reports/waive", { event_id: ev.event_id, technician_id: t.technician_id, waived: !t.waived }); toast.success(t.waived ? "Report richiesto di nuovo" : "Report annullato"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const unlock = async (id) => {
    if (!window.confirm("Sbloccare il report? Il tecnico potrà correggerlo e reinviarlo.")) return;
    try { await api.put(`/reports/${id}/unlock`); toast.success("Report sbloccato"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="reports-panel">
      <h2 className="font-display text-xl font-semibold">Report eventi</h2>
      <ReportSettings />
      <div className="flex items-center gap-1" data-testid="reports-month-nav">
        <Button size="icon" variant="ghost" data-testid="reports-prev-month" onClick={() => shift(-1)}><ChevronLeft className="w-5 h-5" /></Button>
        <span className="text-sm font-semibold w-36 text-center capitalize" data-testid="reports-month">{new Date(`${ym}-15`).toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</span>
        <Button size="icon" variant="ghost" data-testid="reports-next-month" onClick={() => shift(1)}><ChevronRight className="w-5 h-5" /></Button>
      </div>
      <div className="flex gap-2" data-testid="reports-subtabs">
        {[["pending", "Da ricevere", pendingCount], ["received", "Ricevuti", monthList.length]].map(([id, label, n]) => (
          <button key={id} data-testid={`reports-subtab-${id}`} onClick={() => setSub(id)}
            className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${sub === id ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-200" : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200"}`}>
            {label} <span className={`ml-1 text-[11px] font-bold rounded-full px-1.5 py-0.5 ${id === "pending" && n ? "bg-rose-500/20 text-rose-300" : "bg-slate-800 text-slate-400"}`}>{n}</span>
          </button>
        ))}
      </div>
      {sub === "pending" && <MissingReports list={monthMissing} onToggle={toggle} onNudge={nudge} />}
      {sub === "pending" && otherPending > 0 && <p className="text-xs text-amber-300" data-testid="reports-other-pending">Altri {otherPending} report da ricevere in altri mesi.</p>}
      {sub === "received" && list && monthList.length === 0 && <p className="text-sm text-slate-500">Nessun report ricevuto per gli eventi di questo mese.</p>}
      {sub === "received" && monthList.map((r) => <ReportCard key={r.id} r={r} onUnlock={unlock} />)}
    </div>
  );
}
